import millify from "millify";
import pluralize from "pluralize";
import sanitizeHtml from "sanitize-html";

import {url} from "./url"
import router from './router'
import {createSimpleFilter} from "./filterConfigs";
import {entityConfigs} from "@/entityConfigs";
import {toPrecision} from "./util";
import * as openalexId from "@/openalexId";

const filters = {
  entityWorksLink(id, entityData) {
    const entityType = openalexId.getEntityType(id);
    if (!id || !entityType) { return; }
    const shortId = openalexId.getShortId(id);
    // For repository sources, use locations.source.id to show all works
    // where this repo appears in any location (not just primary)
    const isRepository = entityType === 'sources' && entityData?.type === 'repository';
    const filterKey = isRepository ? 'locations.source.id' : entityConfigs[entityType].filterKey;
    const filter = createSimpleFilter(
      "works",
      filterKey,
      shortId,
    );
    return {
      name: "Serp",
      params: { entityType: "works" },
      query: { filter: filter.asStr, include_xpac: 'true' },
    };
  },
  /**
   * Create a link to the awards SERP filtered by a funder.
   * @param {string} funderId - The funder's OpenAlex ID
   * @returns {Object} - Vue router link object
   */
  funderAwardsLink(funderId) {
    if (!funderId) { return; }
    const shortId = openalexId.getShortId(funderId);
    const filter = createSimpleFilter(
      "awards",
      "funder.id",
      shortId,
    );
    return {
      name: "Serp",
      params: { entityType: "awards" },
      query: { filter: filter.asStr },
    };
  },
  entityZoomLink(id) {
    if (!id) { return; }
    const parsed = openalexId.parseId(id);
    if (!parsed) { return; }

    const route = router.currentRoute.value;
    const isWorksSerp = route.name === "Serp" && route.params?.entityType === "works";
    const targetIsWork = parsed.entityType === "works";
    // On xs viewports (<600px — phones), bypass the zoom drawer entirely and
    // link straight to the entity page. The drawer is too cramped on phones
    // and the canonical page is the better destination. Tablets (sm+) keep
    // the drawer.
    const isPhoneViewport = typeof window !== 'undefined' && window.innerWidth < 600;
    if (isWorksSerp && targetIsWork && !isPhoneViewport) {
      const zoomValue = parsed.shortId;
      const newQuery = url.addToQuery(route.query, "zoom", zoomValue);
      return {
        name: "Serp",
        params: { ...route.params },
        query: newQuery,
      };
    }
    return {
      name: "EntityPage",
      params: { entityType: parsed.entityType, entityId: parsed.shortId },
    };
  },
  zoomLink(fullId) {
    if (!fullId) { return; }
    const parsed = openalexId.parseId(fullId);
    if (!parsed) { return; }
    const zoomValue = parsed.isNative ? parsed.shortId : parsed.normalized;
    const zoomIds = router.currentRoute.value.query.zoom?.split(",") ?? [];
    zoomIds.push(zoomValue);
    const newQuery = url.addToQuery(router.currentRoute.value.query, "zoom", zoomIds.join());
    return {
      name: "Serp",
      query: newQuery,
    };
  },
  toPrecision(number, precision = 4) {
    return toPrecision(number, precision);
  },
  truncate(str, length = 100) {
    str = String(str);
    if (str.length > length) {
      return str.slice(0, length) + '...';
    }
    return str;
  },
  pluralize(str, count) {
    let pluralized = pluralize(str, count);
    fixedCaseWords.map(word => {
      if (pluralized.toLowerCase() === word.toLowerCase()) {
        pluralized = word;
      }
    })
    return pluralized;
  },
  capitalize(str) {
    if (typeof str !== "string") { return str; }
    const firstLetter = str[0];
    return firstLetter.toUpperCase() + str.substring(1);
  },
  titleCase(str) {
    if (typeof str !== "string") return str;
    const fixedWordsMap = fixedCaseWords.reduce((map, word) => {
      map[word.toLowerCase()] = word;
      return map;
    }, {});
    return str
      .split(" ")
      .map((word, index) => {
        const lowerCaseWord = word.toLowerCase();
        if (fixedWordsMap[lowerCaseWord]) {
          return fixedWordsMap[lowerCaseWord];
        }
        if (index === 0 || !stopWords.includes(lowerCaseWord)) {
          return word[0].toUpperCase() + word.slice(1).toLowerCase();
        }
        return lowerCaseWord;
      })
      .join(" ");
  },
  prettyName(name) {
    let ret = name
      .replace("ieee", "IEEE")
      .replace("United States of America", "United States")
      .replace("United Kingdom of Great Britain and Northern Ireland", "United Kingdom");
    const typeRe = /[a-z]+-[a-z]+/;
    if (typeRe.test(ret)) { ret = ret.replace("-", " "); }
    return ret;
  },
  prettyTitle(title, facetKey) {
    if (!title) return "Untitled"
    if (/^\s+$/.test(title)) return "Untitled"
    if (title && title.toUpperCase() === title && title.length > 15) {
        title = this.titleCase(title.toLowerCase());
    }
    if (facetKey && facetKey === "type") {
        title = title.replace("-", " ")
        title = this.titleCase(title)
    }
    if (facetKey === "authorships.institutions.country_code") {
        title = title
            .replace("ieee", "IEEE")
            .replace("United States of America", "United States")
            .replace("United Kingdom of Great Britain and Northern Ireland", "United Kingdom")
    }

    const safeTitle = sanitizeHtml(title, {
        allowedTags: ['b', 'i', 'em', 'strong', 'a'],
    })
    return safeTitle
  },
  millify(number) {
    return millify(
      number,
      {
        precision: 0,
        lowercase: false,
      }
    );
  },
  /**
   * Get display title for an award entity.
   * Awards may not have a display_name/title, so we fall back to funder_award_id,
   * then to the short OpenAlex ID (e.g., "G5453342221").
   * @param {Object} award - The award entity object
   * @returns {string} - The display title to show
   */
  getAwardDisplayTitle(award) {
    if (!award) return 'Untitled';
    // Try display_name first (may be set by API hack), then title
    if (award.display_name) return award.display_name;
    if (award.title) return award.title;
    // Fall back to funder_award_id if available
    if (award.funder_award_id) return award.funder_award_id;
    // Last resort: use short OpenAlex ID
    if (award.id) {
      return openalexId.toDisplayFormat(award.id, 'short');
    }
    return 'Untitled';
  },
};

const stopWords = [
  "a", "an", "and", "as", "at", "but", "by", "for", "in",
  "nor", "of", "on", "or", "so", "the", "to", "up", "yet"
];
const fixedCaseWords = [
  "OpenAlex", "ID", "IDs", "ROR", "ORCID", "DOI", "OA", "ISSN", "ISSNs",
  "DOAJ", "SDG", "SDGs", "FWCI", "URL", "CrossRef"
];

export default filters;